/**
 * Marker, um im Browser zu prüfen, welcher Stand tatsächlich geladen ist.
 * Erscheint einmal beim Laden des Moduls in der Konsole.
 * Kann entfernt werden, sobald das Deployment sitzt.
 */
export const INJECT_CONFIG_VERSION = "2026-08-18 indexOnly + data-search-href";
console.info("[inject] config", INJECT_CONFIG_VERSION);

/**
 * Gemeinsamer Selektor für alle injizierbaren Kacheln.
 * Bewusst nicht mehr an ".weekly" gebunden, damit auch Bundle-Kacheln
 * (a.weekly.bundle) oder künftige Varianten erfasst werden.
 */
export const INJECT_ANCHOR_SELECTOR = "a[data-inject-id]";

/**
 * Container einer Kachel. Suchergebnisse rendern pro Treffer sowohl
 * ".weeklyColumn" als auch ".bundleColumn"; entfernt wird immer der
 * ganze Container, nicht nur der Anchor.
 */
export const COLUMN_SELECTOR = ".weeklyColumn, .bundleColumn, .item";

/** Fallback-ID des JSON-Scripts auf der Detailseite. */
export const DEFAULT_JSON_ID = "page-data";

const WEEKLY_V2_MAPPINGS = [
    { from: "headline",        to: ".weekly__headline",        mode: "text" },
    { from: "cta",             to: ".weekly__cta",             mode: "text" },
    { from: "content.html",    to: ".weekly__content",         mode: "html", collapse: ".weekly__content" },
    { from: "state.html",      to: ".weekly__stateText",       mode: "html", collapse: ".weekly__state" },
    { from: "school.html",     to: ".weekly__schoolText",      mode: "html", collapse: ".weekly__school" },
    { from: "subject.html",    to: ".weekly__subjectText",     mode: "html", collapse: ".weekly__subject" },
    { from: "class.html",      to: ".weekly__classText",       mode: "html", collapse: ".weekly__class" },
    { from: "textbook.html",   to: ".weekly__textbookTitle",   mode: "attr", attr: "data-original-title" },
    { from: "logo.src",        to: ".weekly__logoImg",         mode: "attr", attr: "src" },
    { from: "logo.alt",        to: ".weekly__logoImg",         mode: "attr", attr: "alt" },
    { from: "logo.width",      to: ".weekly__logoImg",         mode: "attr", attr: "width" },
    { from: "logo.height",     to: ".weekly__logoImg",         mode: "attr", attr: "height" },
    { from: "issue",           to: ".weekly__issue",           mode: "text", collapse: ".weekly__date" },
    { from: "group.html",      to: ".weekly__group--item",     mode: "html", collapse: ".weekly__group--item" },
    { from: "image.src",       to: ".weekly__imageImg",        mode: "attr", attr: "src" },
    { from: "image.alt",       to: ".weekly__imageImg",        mode: "attr", attr: "alt" },
    { from: "image.width",     to: ".weekly__imageImg",        mode: "attr", attr: "width" },
    { from: "image.height",    to: ".weekly__imageImg",        mode: "attr", attr: "height" },
];

/**
 * Bundle-Kacheln (collection_bundle_list.php).
 *
 * Unterschiede zu weekly-v2:
 * - statt "content.html" gibt es "summary.html"
 * - der Zähler-Block ".weekly__bundle" wird als fertiges HTML geliefert
 * - ".weekly__date" enthält den statischen Text "Bundle" und darf deshalb
 *   nicht überschrieben werden -> mode "presence" steuert nur das Collapse
 * - der Group-Badge liegt auf ".weekly__group--item.bundle"
 */
const BUNDLE_V2_MAPPINGS = [
    { from: "headline",     to: ".weekly__headline",           mode: "text" },
    { from: "cta",          to: ".weekly__cta",                mode: "text", collapse: ".weekly__cta" },
    { from: "summary.html", to: ".weekly__summary",            mode: "html", collapse: ".weekly__summary" },
    { from: "group.html",   to: ".weekly__group--item.bundle", mode: "html", collapse: ".weekly__group" },
    // "bundle" liefert den statischen Text "Bundle" fuer das Datums-Badge.
    { from: "bundle",       to: ".weekly__date",               mode: "text", collapse: ".weekly__date" },

    // Zaehler-Items einzeln. Jedes Item verschwindet, wenn sein HTML leer ist;
    // bleibt keines uebrig, raeumt collapseEmptyContainers ".weekly__bundle" weg.
    { from: "unterrichtseinheit.html", to: ".weekly__bundle--item.unterrichtseinheit", mode: "html", collapse: ".weekly__bundle--item.unterrichtseinheit" },
    { from: "infografik.html",         to: ".weekly__bundle--item.infografik",         mode: "html", collapse: ".weekly__bundle--item.infografik" },
    { from: "karikatur.html",          to: ".weekly__bundle--item.karikatur",          mode: "html", collapse: ".weekly__bundle--item.karikatur" },
    { from: "statistik.html",          to: ".weekly__bundle--item.statistik",          mode: "html", collapse: ".weekly__bundle--item.statistik" },
    { from: "erklaerfilm.html",        to: ".weekly__bundle--item.erklaerfilm",        mode: "html", collapse: ".weekly__bundle--item.erklaerfilm" },

    { from: "logo.src",     to: ".weekly__logoImg",            mode: "attr", attr: "src" },
    { from: "logo.alt",     to: ".weekly__logoImg",            mode: "attr", attr: "alt" },
    { from: "logo.width",   to: ".weekly__logoImg",            mode: "attr", attr: "width" },
    { from: "logo.height",  to: ".weekly__logoImg",            mode: "attr", attr: "height" },
    { from: "image.src",    to: ".weekly__imageImg",           mode: "attr", attr: "src" },
    { from: "image.alt",    to: ".weekly__imageImg",           mode: "attr", attr: "alt" },
    { from: "image.width",  to: ".weekly__imageImg",           mode: "attr", attr: "width" },
    { from: "image.height", to: ".weekly__imageImg",           mode: "attr", attr: "height" },
];

/**
 * Map-Deskriptoren.
 * Ein Eintrag darf weiterhin ein reines Array sein (Abwärtskompatibilität)
 * oder ein Objekt mit zusätzlichen Optionen:
 *   jsonId        – ID des JSON-Scripts auf der Zielseite (Stufe 1)
 *   jsonSelector  – CSS-Fallback auf der Zielseite
 *   indexUrl      – feste URL der Übersichtsseite für den Fallback (Stufe 2)
 *   indexOnly     – überspringt Stufe 1 komplett: die Daten stehen nur auf
 *                   der Übersichtsseite, die Detailseite wird nie geladen
 *   indexSegment  – alternativ: Segment, aus dem die URL abgeleitet wird
 *                   (/portal/de/irankrieg/ -> /portal/de/bundles/)
 *   itemAttr      – Attribut, über das die Kachel auf der Übersicht
 *                   wiedergefunden wird
 *   itemJsonSelector – JSON-Script innerhalb dieser Kachel
 *   itemLinkAttr  – Attribut der Kachel, dessen Wert als neuer href
 *                   des Anchors gesetzt wird
 *   requireAny    – Felder, von denen mindestens eines gefüllt sein muss,
 *                   sonst wird die Kachel entfernt
 *   ensureTrailingSlash – ergänzt im href der Kachel einen "/" vor der Query
 *                   (/portal/de/irankrieg?search=1 ->
 *                    /portal/de/irankrieg/?search=1). Gilt für den Fetch,
 *                   für den sichtbaren Link und für den Link aus itemLinkAttr.
 */
export const INJECT_MAPS = {
    "weekly-v2": {
        jsonId: DEFAULT_JSON_ID,
        // Pro Suchtreffer werden weeklyColumn und bundleColumn gerendert.
        // Bevor die Detailseite geladen wird, prüft die Weekly-Kachel über
        // bundle-v2, ob die URL ein Bundle ist. Wenn ja, entfernt sie sich
        // selbst – ohne Request auf eine Seite, die es nicht gibt.
        deferToMapId: "bundle-v2",
        // Ist es ein echtes Weekly, fliegt die bundleColumn raus.
        removeSiblings: true,
        mappings: WEEKLY_V2_MAPPINGS,
    },

    "bundle-v2": {
        // Die Daten stehen NIE auf der Zielseite des Links, sondern immer
        // auf der Übersicht /portal/de/bundles/. Stufe 1 (Detailseite laden
        // und dort page-data lesen) wird deshalb übersprungen – sie würde
        // für /portal/de/irankrieg?search=1 nur einen 404 erzeugen.
        indexOnly: true,

        // Findet die Kachel ihr Item auf /portal/de/bundles/, handelt es sich
        // um ein Bundle: die parallel gerenderte weeklyColumn mit derselben
        // URL wird entfernt, bevor sie ihre Detailseite anfragt.
        removeSiblings: true,

        // Übersicht /portal/de/bundles/ laden, dort die Kachel mit
        // data-search-href="/portal/de/irankrieg?search=1" suchen und deren
        // <script class="item-data"> auslesen. Der Vergleich läuft über
        // normalizeHref, ein "/" vor der Query spielt dabei keine Rolle.
        indexSegment: "bundles",
        itemAttr: "data-search-href",
        itemJsonSelector: "script.item-data",
        // Der endgültige Link der Kachel kommt aus data-link des Items.
        itemLinkAttr: "data-link",

        requireAny: ["headline", "image.src"],
        mappings: BUNDLE_V2_MAPPINGS,
    },
};

/**
 * Normalisiert einen Map-Eintrag auf die Objektform.
 */
export function getInjectMap(mapId) {
    const raw = mapId ? INJECT_MAPS[mapId] : null;
    if (!raw) return null;

    const def = Array.isArray(raw) ? { mappings: raw } : raw;
    if (!Array.isArray(def.mappings) || def.mappings.length === 0) return null;

    return {
        jsonId: def.jsonId || DEFAULT_JSON_ID,
        jsonSelector: def.jsonSelector || null,
        indexUrl: def.indexUrl || null,
        indexSegment: def.indexSegment || null,
        itemAttr: def.itemAttr || "data-href",
        itemJsonSelector: def.itemJsonSelector || "script.item-data",
        itemLinkAttr: def.itemLinkAttr || "data-link",
        requireAny: Array.isArray(def.requireAny) ? def.requireAny : null,
        // Opt-in: überspringt die Detailseite und liest direkt auf der Übersicht.
        indexOnly: def.indexOnly === true,
        // Opt-in: entfernt bei Erfolg die Partner-Kachel mit derselben URL.
        removeSiblings: def.removeSiblings === true,
        // Optional: löst diese Map zuerst; findet sie Daten, gibt die
        // aktuelle Kachel auf und entfernt sich.
        deferToMapId: def.deferToMapId || null,
        // Opt-in, damit bestehende Maps unverändert bleiben.
        // Für weekly-v2 genügt bei Bedarf ebenfalls "ensureTrailingSlash: true".
        ensureTrailingSlash: def.ensureTrailingSlash === true,
        mappings: def.mappings,
    };
}
